import { Box } from '@mui/material';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CommonDatagrid } from '..';
import { IPermissionsContent, IRootState } from '../../../types/store';
import { getPermissionsColumns } from './columns';
import { AddNewPermissionDialog } from '../../Dialog/AddNewPermissionDialog';
import { EditPermissionDialog } from '../../Dialog/EditPermissionDialog';
import { ViewPermissionDialog } from '../../Dialog/ViewPermissionDialog';
import { RefreshButton } from '../../Buttons/RefreshButton';
import { PermissionsService } from '../../../api/services/permissionsService';
import { removePermission, setPermissions } from '../../../store/Reducers/PermissionsSlice';
import { showAlertDialog } from '../../../store/Reducers/AlertDialogSlice';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';

export const PermissionsDatagrid = () => {
  const { content } = useSelector((state: IRootState) => state.permissions);
  const [selectedPermission, setSelectedPermission] = useState<IPermissionsContent | null>(null);
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
  const [isOpenViewDialog, setIsOpenViewDialog] = useState(false);

  const dispatch = useDispatch();

  const onClickEdit = (permission: IPermissionsContent) => {
    setSelectedPermission(permission);
    setIsOpenEditDialog(true);
  };

  const onClickView = (permission: IPermissionsContent) => {
    setSelectedPermission(permission);
    setIsOpenViewDialog(true);
  };

  const reloadData = async () => {
    const res = await PermissionsService.getAllPermissions();
    dispatch(setPermissions(res));
  };

  const deletePermission = async (id: number) => {
    try {
      await PermissionsService.deletePermission(id);
      dispatch(removePermission(id));
      dispatch(showNotification({ message: 'Successfully deleted Permission.' }));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to delete Permission. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const onClickDelete = (permission: IPermissionsContent) => {
    dispatch(
      showAlertDialog({
        title: 'Delete Permission?',
        description: 'This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        onConfirm: () => {
          deletePermission(permission.id);
        },
      }),
    );
  };

  const permissionsColumns = useMemo(() => {
    return getPermissionsColumns(onClickDelete, onClickEdit, onClickView);
  }, []);

  return (
    <>
      <CommonDatagrid
        header={<p>Permissions</p>}
        data={content}
        columns={permissionsColumns}
        rowId={'id'}
        searchbarPlaceholder={'Search permission by name'}
        searchKey={'name'}
        headerActionButtons={
          <Box display="flex" gap={2}>
            <RefreshButton onClick={reloadData} />
            <AddNewPermissionDialog />
          </Box>
        }
      />
      <EditPermissionDialog
        selectedPermission={selectedPermission}
        isOpen={isOpenEditDialog}
        onClose={() => {
          setIsOpenEditDialog(false);
          setSelectedPermission(null);
        }}
      />
      <ViewPermissionDialog
        permission={selectedPermission}
        isOpen={isOpenViewDialog}
        onClose={() => {
          setIsOpenViewDialog(false);
          setSelectedPermission(null);
        }}
      />
    </>
  );
};
